import { createApi } from '@reduxjs/toolkit/query/react';
import baseQuery from '../../Shared/API/config';
import { studentsApi } from './StudentsApi';
import { ApplicableDiscount } from '../../Shared/types/types';

export type PreviousTermPaymentOption =
  | { eligible: false }
  | {
      eligible: true;
      previousTerm: { displayName: string };
      currentTerm: { displayName: string; startDate: string };
      amountOwing: string;
    };

export const studentPaymentApi = createApi({
  reducerPath: 'studentPaymentApi',
  baseQuery,
  tagTypes: ['studentPayment', 'studentSummary', 'Student'],
  endpoints: builder => ({
    getStudentPaymentSummary: builder.query<any, { studentId: string }>({
      query: ({ studentId }) =>
        `/billing/students/${studentId}/account/summary`,
      providesTags: ['studentSummary'],
    }),
    getPreviousTermPaymentOption: builder.query<
      PreviousTermPaymentOption,
      { studentId: string }
    >({
      query: ({ studentId }) =>
        `/billing/students/${studentId}/payments/previous-term-option`,
      providesTags: ['studentPayment'],
    }),
    getBillPreview: builder.query<any, string>({
      query: studentId => `/billing/students/${studentId}/bill-preview`,
    }),
    getStudentPayments: builder.query<
      any,
      { studentId: string; receiptId: string }
    >({
      query: ({ studentId, receiptId }) =>
        `/billing/students/${studentId}/payments?q=${receiptId}`,
      providesTags: ['studentPayment'],
    }),
    makeStudentPayment: builder.mutation({
      query: ({ newPayment, studentId }) => ({
        url: `/billing/students/${studentId}/payments`,
        method: 'POST',
        body: newPayment,
      }),
      invalidatesTags: ['studentPayment', 'studentSummary', 'Student'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(studentsApi.util.invalidateTags(['Subscriptions']));
        } catch (e) {
          console.error(e);
        }
      },
    }),
    cancelPayment: builder.mutation({
      query: ({ reason, studentId, paymentId }) => ({
        url: `/billing/students/${studentId}/payments/${paymentId}/cancel`,
        method: 'POST',
        body: reason,
      }),
      invalidatesTags: ['studentPayment', 'studentSummary', 'Student'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(studentsApi.util.invalidateTags(['Subscriptions']));
        } catch (e) {
          console.error(e);
        }
      },
    }),
    // Mints a new digital-receipt link on every call, so
    // the success step calls it once and keeps the url for reopening. Not
    // retried: a resend would mint a second link.
    createReceiptLink: builder.mutation<
      { url: string },
      { studentId: string; paymentId: number }
    >({
      query: ({ studentId, paymentId }) => ({
        url: `/billing/students/${studentId}/payments/${paymentId}/receipt-link`,
        method: 'POST',
      }),
    }),
    writeOffArrears: builder.mutation<
      any,
      { studentId: string; sessionId?: number; remarks?: string }
    >({
      query: ({ studentId, ...body }) => ({
        url: `/billing/students/${studentId}/debt-write-off`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['studentPayment', 'studentSummary', 'Student'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(studentsApi.util.invalidateTags(['Subscriptions']));
        } catch (e) {
          console.error(e);
        }
      },
    }),
    getStudentSalesOrders: builder.query<
      any,
      {
        studentId: string;
        page?: number;
        page_size?: number;
        search?: string;
      }
    >({
      query: ({ studentId, page = 1, page_size = 25, search = '' }) =>
        `/finance/sales/students/${studentId}/orders?page=${page}&page_size=${page_size}&q=${search}`,
      providesTags: ['studentPayment'],
    }),
    calculateDiscount: builder.mutation<
      { method: string; value: number; baseAmount: number; discountAmount: number },
      { studentId: string; method: string; amount: number; saleAmount?: number }
    >({
      query: ({ studentId, ...body }) => ({
        url: `/billing/students/${studentId}/calculate-discount`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: any) => {
        // Endpoint returns fields at the top level; tolerate a { data: ... }
        // envelope too in case it is ever wrapped like other billing endpoints.
        const payload = response?.data ?? response;
        return {
          method: payload.method,
          value: Number(payload.value),
          baseAmount: Number(payload.baseAmount),
          discountAmount: Number(payload.discountAmount),
        };
      },
    }),
    getStudentApplicableDiscounts: builder.query<
      ApplicableDiscount[],
      { studentId: string }
    >({
      query: ({ studentId }) =>
        `/billing/students/${studentId}/applicable-discounts`,
      transformResponse: (response: { data: ApplicableDiscount[] }) =>
        response.data.map(discount => ({
          ...discount,
          amount: Number(discount.amount),
          discountAmount: discount.discountAmount ? Number(discount.discountAmount) : undefined,
          minRequiredAmount: discount.minRequiredAmount ? Number(discount.minRequiredAmount) : undefined,
        })),
    }),
  }),
});

export const {
  useGetStudentPaymentSummaryQuery,
  useGetPreviousTermPaymentOptionQuery,
  useGetBillPreviewQuery,
  useGetStudentPaymentsQuery,
  useMakeStudentPaymentMutation,
  useCancelPaymentMutation,
  useCreateReceiptLinkMutation,
  useWriteOffArrearsMutation,
  useGetStudentSalesOrdersQuery,
  useGetStudentApplicableDiscountsQuery,
  useCalculateDiscountMutation,
} = studentPaymentApi;
